// ============================================================
// Datei: src/pages/ressorts/kasse/Kasse.jsx
//
// PURPOSE:
// - Hauptseite für das Ressort Kasse
// - normale Kasse-Ansicht lädt nur Kasse-Helfer und Kasse-Schichten
// - Adminansicht innerhalb Kasse lädt zusätzlich alle Volunteers,
//   alle Schichten und alle Gäste
//
// ÄNDERUNGEN:
// - reloadAdminData ergänzt
// - selectedVolunteerEditMode ergänzt
// - openVolunteerEdit / closeVolunteerEdit ergänzt
// - setAllVolunteers wird an Desktop/Mobile weitergegeben
// - Adminansicht kann EditVolunteerModal im Adminmodus öffnen
// ============================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import "../../../styles/global.css";
import "../../../styles/ressortTabs.css";
import "../../../styles/ccMailCard.css";

import useVolunteers from "../../../components/fetch/useVolunteers";
import useShifts from "../../../components/fetch/useShifts";
import Api from "../../../Api";

import KasseDesktop from "./KasseDesktop";
import KasseMobile from "./KasseMobile";
import useMediaQuery from "../../../hooks/useMediaQuery";

const Kasse = () => {
	const isMobile = useMediaQuery("(max-width: 768px)");
	const navigate = useNavigate();

	// ============================================================
	// KASSE-VOLUNTEERS
	// ============================================================
	const volunteersHook = useVolunteers("kasse");
	const volunteers = volunteersHook[0];
	const setVolunteers = volunteersHook[1];

	const reloadVolunteersFromBackend = useCallback(async () => {
		try {
			const res = await Api.get("/volunteers?department=kasse");
			setVolunteers(Array.isArray(res.data) ? [...res.data] : []);
		} catch (err) {
			console.error("❌ Kasse: Fehler beim Laden der Kasse-Volunteers:", err);
			setVolunteers([]);
		}
	}, [setVolunteers]);

	const reloadVolunteers =
		typeof volunteersHook[2] === "function"
			? volunteersHook[2]
			: reloadVolunteersFromBackend;

	// ============================================================
	// KASSE-SCHICHTEN
	// ============================================================
	const [shifts] = useShifts("kasse");

	// ============================================================
	// STATE
	// ============================================================
	const [selectedVolunteer, setSelectedVolunteer] = useState(null);
	const [selectedVolunteerEditMode, setSelectedVolunteerEditMode] = useState("ressort");

	const [guests, setGuests] = useState([]);
	const [activeTab, setActiveTab] = useState("overview");

	// ============================================================
	// ADMINANSICHT-DATEN
	// ============================================================
	const [allVolunteers, setAllVolunteers] = useState([]);
	const [allShifts, setAllShifts] = useState([]);

	const loadAllVolunteers = useCallback(async () => {
		try {
			const res = await Api.get("/volunteers");
			setAllVolunteers(Array.isArray(res.data) ? [...res.data] : []);
		} catch (err) {
			console.error("❌ Kasse-Adminansicht: Fehler beim Laden aller Volunteers:", err);
			setAllVolunteers([]);
		}
	}, []);

	const loadAllShifts = useCallback(async () => {
		try {
			const res = await Api.get("/admin/shifts");
			setAllShifts(Array.isArray(res.data) ? [...res.data] : []);
		} catch (err) {
			console.error("❌ Kasse-Adminansicht: Fehler beim Laden aller Schichten:", err);
			setAllShifts([]);
		}
	}, []);

	const loadGuests = useCallback(async () => {
		try {
			const res = await Api.get("/guests");
			setGuests(Array.isArray(res.data) ? res.data : []);
		} catch (err) {
			console.error("❌ Kasse: Fehler beim Laden der Gäste:", err);
			setGuests([]);
		}
	}, []);

	// ============================================================
	// ADMIN-RELOAD
	// ============================================================
	const reloadAdminData = useCallback(async () => {
		await Promise.all([
			reloadVolunteers(),
			loadAllVolunteers(),
			loadAllShifts(),
			loadGuests()
		]);
	}, [reloadVolunteers, loadAllVolunteers, loadAllShifts, loadGuests]);

	// ============================================================
	// EDIT-MODAL ÖFFNEN
	// ============================================================
	const openVolunteerEdit = useCallback((volunteer, mode = "ressort") => {
		setSelectedVolunteerEditMode(mode);
		setSelectedVolunteer(volunteer);
	}, []);

	const closeVolunteerEdit = useCallback(() => {
		setSelectedVolunteer(null);
		setSelectedVolunteerEditMode("ressort");
	}, []);

	// ============================================================
	// SECURITY + INITIAL LOAD
	// ============================================================
	useEffect(() => {
		const role = localStorage.getItem("role");
		const dept = localStorage.getItem("department");

		const isKasseOk = role === "OK" && dept?.toLowerCase() === "kasse";

		if (!isKasseOk) {
			alert("Zugriff verweigert!");
			navigate("/home");
			return;
		}

		loadGuests();
		reloadVolunteers();
		loadAllVolunteers();
		loadAllShifts();
	}, [
		navigate,
		loadGuests,
		reloadVolunteers,
		loadAllVolunteers,
		loadAllShifts
	]);

	// ============================================================
	// CC DOWNLOAD
	// ============================================================
	const downloadCcEmails = async () => {
		try {
			const res = await Api.get("/cc-emails", {
				responseType: "blob"
			});

			const blob = new Blob([res.data], { type: "text/plain" });
			const url = window.URL.createObjectURL(blob);

			const a = document.createElement("a");
			a.href = url;
			a.download = "helferfest_cc_liste.txt";
			document.body.appendChild(a);
			a.click();
			a.remove();

			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error("CC-Download Fehler:", err);
			alert("Download fehlgeschlagen");
		}
	};

	// ============================================================
	// PROPS FÜR DESKTOP + MOBILE
	// ============================================================
	const layoutProps = {
		volunteers,
		setVolunteers,
		reloadVolunteers,
		shifts,
		guests,
		reloadGuests: loadGuests,
		selectedVolunteer,
		selectedVolunteerEditMode,
		openVolunteerEdit,
		closeVolunteerEdit,
		activeTab,
		setActiveTab,
		downloadCcEmails,
		allVolunteers,
		setAllVolunteers,
		allShifts,
		reloadAdminData
	};

	return (
		<div
			className="kasse-page bg-reveal"
			data-bg={`${process.env.PUBLIC_URL}/backgrounds/background_kasse.png`}
		>
			<h2 className="kasse-heading">Kasse</h2>

			{isMobile ? (
				<KasseMobile {...layoutProps} />
			) : (
				<KasseDesktop {...layoutProps} />
			)}
		</div>
	);
};

export default Kasse;
